import React, { useCallback, useState, useMemo } from 'react';
import axios from 'axios';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFlexLayout, useRowSelect, useSortBy, useTable } from 'react-table';

import { CollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section';
import { CollapsibleListSectionContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content';
import { CollapsibleListSectionHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-header';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import messages from './messages';
import style from './style.module.less';
import { DataDrivenLogo } from '../../../components/data-driven-logo';
import { FormattedCurrency } from '../../../components/formatted-currency';
import { SimpleModal } from '../../../components/simple-modal';
import { emptyObject } from 'ui-components/default-props/empty';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import { VIEWED_STATUS } from '../automation-reccomendation-constants';
import { ContactSalesButton } from '../contact-sales-button';

/**
 * @param {object} props
 * @param {string} props.value
 * @returns {ReactNode}
 */
const ShipDateColumn = ({ value }) => {
  const intl = useIntl();
  const formattedDate = intl.formatDate(value, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return <span>{formattedDate}</span>;
};

const COLUMN_DEFINITIONS = [
  {
    accessor: 'shipDate',
    Header: <FormattedMessage {...messages.shipDate} />,
    Cell: ({ value }) => {
      return <ShipDateColumn value={value} />;
    },
    width: 1,
  },
  {
    accessor: 'orderNumber',
    Header: <FormattedMessage {...messages.orderNumber} />,
    width: 1,
  },
  {
    accessor: 'trackingNumber',
    Header: <FormattedMessage {...messages.trackingNumber} />,
    width: 1,
  },
  {
    accessor: 'shippingAmount',
    Header: <FormattedMessage {...messages.shippingAmount} />,
    Cell: ({ value, row }) => {
      const { currencyCode } = row.original;
      return <FormattedCurrency code={currencyCode} value={value} />;
    },
    width: 1,
  },
  {
    accessor: 'projectedSavings',
    Header: <FormattedMessage {...messages.potentialSavingsColumnTitle} />,
    Cell: ({ value }) => {
      return <FormattedCurrency code={value.currency} value={value.amount} />;
    },
    width: 1,
  },
];

/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {() => void} props.openContactSalesModal
 * @param {number | string} props.id
 * @returns {JSX.Element}
 */
export const ExplanationModal = ({ onClose, id, openContactSalesModal }) => {
  const [loading, setLoading] = useAsyncSafeState(true);
  const [errorMessage, setErrorMessage] = useAsyncSafeState('');
  const [data, setData] = useAsyncSafeState({});
  const [tableSectionIsOpen, setTableSectionIsOpen] = useState(false);

  const getExplanationData = useCallback(async () => {
    setErrorMessage('');
    setLoading(true);
    try {
      const response = await axios.get(`/automationRecommendations/${id}`);
      setData(response.data);
      axios.post(`/automationRecommendations/update-status/${id}`, {
        statusId: VIEWED_STATUS,
      });
      setLoading(false);
    } catch (e) {
      setErrorMessage(e.message);
      setLoading(false);
    }
  }, [id, setData, setErrorMessage, setLoading]);

  useOnComponentMount(() => {
    getExplanationData();
  });

  const { thirtyDayProjectedSavings, recommendedChange, thirtyDayActualSpend } = data;

  const { serviceFromName, serviceFromProviderId, serviceToName, serviceToProviderId, topShipments } = recommendedChange || emptyObject;

  const shipments = useMemo(
    () =>
      (topShipments || []).map((s) => ({
        ...s,
        currencyCode: thirtyDayActualSpend?.code,
      })),
    [thirtyDayActualSpend, topShipments],
  );

  const projectedSpend = thirtyDayActualSpend?.value - thirtyDayProjectedSavings?.value;

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns: COLUMN_DEFINITIONS,
      data: shipments,
    },
    useSortBy,
    useFlexLayout,
    useRowSelect,
  );

  return (
    <SimpleModal headerMessage={messages.modalTitle} onClose={onClose} size='large'>
      <FlexColumn className={style.body}>
        {loading ? (
          <div className={style['loading-indicator']}>
            <LoadingIndicator />
          </div>
        ) : null}
        {!loading && errorMessage ? <Text className={style.red} textType={TextType.H3} value={errorMessage} /> : null}
        {!loading && !errorMessage && (
          <FlexColumn className={style.content}>
            <Text textType={TextType.H3} value={messages.subtitle} />
            <FlexColumn className={style['information-section']}>
              <Text
                className={style['based-on']}
                formattedMessageValues={{
                  numberOfShipments: shipments.length,
                  serviceName: serviceFromName,
                }}
                textType={TextType.Paragraph}
                value={messages.basedOn}
              />
              <FlexRow className={style.row}>
                <FlexRow>
                  <Text className={style['you-spent']} textType={TextType.Paragraph} value={messages.youSpent} />
                  <Text className={style.disclaimer} textType={TextType.Paragraph} value={messages.last30Days} />
                </FlexRow>
                <FlexRow className={style['service-and-price']}>
                  <FlexRow className={style['service-display']}>
                    <DataDrivenLogo logoType='icon' providerId={serviceFromProviderId} />
                    <Text textType={TextType.Paragraph} value={serviceFromName || ''} />
                  </FlexRow>
                  <Text
                    textType={TextType.Paragraph}
                    value={<FormattedCurrency currencyCode={thirtyDayActualSpend.code} value={thirtyDayActualSpend.value} />}
                  />
                </FlexRow>
              </FlexRow>
              <FlexRow className={style.row}>
                <Text
                  formattedMessageValues={{ serviceName: serviceToName }}
                  textType={TextType.Paragraph}
                  value={messages.ifYouSwitched}
                />
                <FlexRow className={style['service-and-price']}>
                  <FlexRow className={style['service-display']}>
                    <DataDrivenLogo logoType='icon' providerId={serviceToProviderId} />
                    <Text textType={TextType.Paragraph} value={serviceToName} />
                  </FlexRow>
                  <Text
                    textType={TextType.Paragraph}
                    value={<FormattedCurrency currencyCode={thirtyDayActualSpend.code} value={projectedSpend} />}
                  />
                </FlexRow>
              </FlexRow>
              <FlexColumn>
                <div className={style['fancy-row']}>
                  <FlexRow className={style['fancy-row-content']}>
                    <Text textType={TextType.Paragraph} value={messages.potentialSavings} />
                    <div className={style['big-number']}>
                      <FormattedCurrency currencyCode={thirtyDayProjectedSavings.currencyCode} value={thirtyDayProjectedSavings.value} />
                    </div>
                  </FlexRow>
                </div>
                <Text className={style.disclaimer} textType={TextType.Paragraph} value={messages.disclaimer} />
              </FlexColumn>
            </FlexColumn>
            <Text className={style['center-text']} textType={TextType.H3} value={messages.ctaTitle} />
            <div className={style['center-text']}>
              <ContactSalesButton buttonText={messages.cta} onClick={openContactSalesModal} />
            </div>

            <CollapsibleListSection open={tableSectionIsOpen}>
              <CollapsibleListSectionHeader
                className={style['collapsible-header']}
                onClick={() => setTableSectionIsOpen(!tableSectionIsOpen)}
                shouldPositionCaretRight
                theme='white'
              >
                <FlexRow className={style['shipment-data-header']}>
                  <Text textType={TextType.H3} value={messages.shipmentData} />
                  <Text className={style['chevron-text']} textType={TextType.Paragraph} value={messages.chevronText} />
                </FlexRow>
              </CollapsibleListSectionHeader>
              <CollapsibleListSectionContent>
                <ReactTableWrapper
                  getTableBodyProps={getTableBodyProps}
                  getTableProps={getTableProps}
                  headerGroups={headerGroups}
                  prepareRow={prepareRow}
                  rows={rows}
                />
              </CollapsibleListSectionContent>
            </CollapsibleListSection>
          </FlexColumn>
        )}
      </FlexColumn>
    </SimpleModal>
  );
};
